<script setup lang="ts">
import { computed, onMounted, reactive, ref, watch } from 'vue';
import api from '@/api';
import { PageName } from '@/api/tracking';
import { MAX_ARTICLE_MEMO_LENGTH } from '@/constants';
import { DgrIcon } from '@stockmarkteam/donguri-ui';
import AdpDocumentExport from '@/components/common/adp-document/adp-document-export.vue';
import InputTag from '@/components/common/atoms/input-tag.vue';
import TextCounter from '@/components/common/molecules/text-counter.vue';
import {
  AdpDocument,
  isArticle,
  isPatent,
  isReport,
  isResearchPaper,
  Tag,
  TeamUser,
} from '@/types';
import { getPageUrl } from '@/utils/adpDocument';
import { extractUrls } from '@/utils/parsers';
import { sanitize } from '@/utils/sanitize';

interface Props {
  adpDocument: AdpDocument;
  creator?: TeamUser;
  tagList?: Tag[];
  editable?: boolean;
  isGroupView?: boolean;
  pageName: PageName;
  groupId?: number;
}

const props = withDefaults(defineProps<Props>(), {
  tagList: () => [],
  editable: () => false,
  isGroupView: () => false,
});

const emit = defineEmits<{
  'article-info-updated': [article: AdpDocument];
  'cancel-edit': [];
  'click-edit': [];
}>();

const editing = ref(false);
const editingMemo = ref(props.adpDocument?.memo ?? '');
const selectedTags = reactive({
  list: [...(props.adpDocument?.tags ?? [])],
});
const displayMemo = ref('');
const tagLabel = computed(() =>
  props.isGroupView ? 'グループタグ' : 'マイタグ',
);
const memoLabel = computed(() =>
  props.isGroupView ? 'グループメモ' : 'マイメモ',
);
const memoPlaceholder = computed(() =>
  props.isGroupView ? 'グループメモを入力' : 'マイメモを入力',
);

onMounted(() => {
  highlightMemoUrl();
});

const updateArticleInfo = () => {
  editing.value = false;
  highlightMemoUrl();

  emit('article-info-updated', {
    ...props.adpDocument,
    memo: editingMemo.value,
    tags: selectedTags.list,
  });
};

const cancelEdit = () => {
  editing.value = false;
  editingMemo.value = props.adpDocument?.memo ?? '';
  selectedTags.list = [...(props.adpDocument?.tags ?? [])];
  emit('cancel-edit');
};

watch(
  () => props.adpDocument,
  () => {
    editing.value = false;
    editingMemo.value = props.adpDocument?.memo ?? '';
    highlightMemoUrl();
    selectedTags.list = [...(props.adpDocument?.tags ?? [])];
  },
);

const clickEdit = () => {
  editing.value = true;
  emit('click-edit');
};

const colorMemoUrl = async () => {
  const previewingArticleId = props.adpDocument?.id;
  const content = await urlParseWithPreview(editingMemo.value);

  if (previewingArticleId !== props.adpDocument?.id) {
    // preview取得中に記事が切り替わっていた場合は置換をやめる
    return;
  }

  displayMemo.value = content;
};

const urlParseWithPreview = async (text: string) => {
  const matches = extractUrls(text);
  if (!matches) return text;
  const uniqueUrls = Array.from(new Set(matches));

  let contents = text;
  try {
    const sites = await Promise.all(
      uniqueUrls.map(url => api.fetchUrlPreview(url)),
    );
    sites.forEach(site => {
      const originalUrl = site.input_url;
      const title = site.title || originalUrl;
      contents = contents.replaceAll(
        originalUrl,
        `<a href="${originalUrl}" class="url-link" target="_blank" rel="noopener noreferrer">${title}</a>`,
      );
    });
  } catch {
    // プレビューに失敗したらtextを返す(中途半端な置換はしない)
    return text;
  }
  return contents;
};

const highlightMemoUrl = () => {
  displayMemo.value = editingMemo.value;
  colorMemoUrl();
};

const adpDocumentPublisher = computed(() => {
  if (props.adpDocument === undefined) return '';
  if (isArticle(props.adpDocument)) {
    return props.adpDocument.media_name;
  } else if (isResearchPaper(props.adpDocument)) {
    return props.adpDocument.journal_name;
  } else if (isPatent(props.adpDocument)) {
    return props.adpDocument.applicant;
  } else if (isReport(props.adpDocument)) {
    return props.adpDocument.site_name;
  }
  return '';
});

const sanitizedDisplayMemo = computed(() => sanitize(displayMemo.value));

const documentUrl = computed(() => getPageUrl(props.adpDocument, false));
</script>

<template>
  <div class="article-info">
    <div v-if="adpDocument" class="basic-info">
      <AdpDocumentExport
        :adp-documents="[adpDocument]"
        :page-name="pageName"
        :group-id="groupId"
      ></AdpDocumentExport>
      <div class="spacing-24"></div>
      <div class="heading">
        <DgrIcon name="newspaper" />
        <div class="spacing-04"></div>
        <div class="c-title-bold--m">基本情報</div>
      </div>
      <div class="article-title">
        <div class="sub-heading c-title-bold--s">タイトル</div>
        <div class="spacing-02"></div>
        <div class="c-text-regular--m">{{ adpDocument.title }}</div>
      </div>
      <div class="spacing-12"></div>
      <div class="article-media">
        <div class="sub-heading c-title-bold--s">メディア</div>
        <div class="c-text-regular--m">{{ adpDocumentPublisher }}</div>
      </div>
      <div class="spacing-12"></div>
      <div class="article-url">
        <div class="sub-heading c-title-bold--s">URL</div>
        <div class="body">
          <a
            class="url-link"
            :href="documentUrl"
            target="_blank"
            rel="noopener noreferrer"
          >
            <div class="url c-text-regular--m">{{ documentUrl }}</div>
            <div>
              <DgrIcon size="small" name="external-link" class="primary" /></div
          ></a>
        </div>
      </div>
      <div class="creator" v-if="creator">
        <div class="spacing-12"></div>
        <div class="sub-heading c-title-bold--s">共有者</div>
        <div class="c-text-regular--m">{{ creator.user_name }}</div>
      </div>
    </div>
    <div v-if="editable" class="edit-tag-memo">
      <div class="spacing-24"></div>
      <div class="tags">
        <div class="heading">
          <DgrIcon name="tag" />
          <div class="spacing-04"></div>
          <div class="c-title-bold--m">{{ tagLabel }}</div>
        </div>
        <div
          class="tags-empty c-text-regular--m"
          v-if="selectedTags.list.length === 0 && !editing"
        >
          未設定
        </div>
        <InputTag
          :placeholder="'入力後、改行でタグを追加'"
          :tag-list="tagList"
          v-model="selectedTags.list"
          :is-editing="editing"
          :is-group="isGroupView"
          :use-label="false"
        ></InputTag>
      </div>
      <div class="spacing-24"></div>
      <div class="memo">
        <div class="heading">
          <DgrIcon name="pencil" />
          <div class="spacing-04"></div>
          <div class="c-title-bold--m">{{ memoLabel }}</div>
        </div>
        <!-- eslint-disable vue/no-v-html -->
        <div
          class="memo-content c-text-regular--m"
          v-if="!editing && displayMemo.length > 0"
          v-html="sanitizedDisplayMemo"
        ></div>
        <!-- eslint-enable vue/no-v-html -->
        <div
          class="memo-empty c-text-regular--m"
          v-if="!editing && displayMemo.length === 0"
        >
          未記入
        </div>
        <textarea
          class="memo-textarea c-textArea"
          v-if="editing"
          v-model.trim="editingMemo"
          :placeholder="memoPlaceholder"
          :maxlength="MAX_ARTICLE_MEMO_LENGTH"
          @keydown.ctrl.enter="updateArticleInfo"
          @keydown.meta.enter="updateArticleInfo"
        ></textarea>
        <TextCounter
          v-if="editing"
          :text="editingMemo"
          :max-length="MAX_ARTICLE_MEMO_LENGTH"
        />
      </div>
      <div class="spacing-24"></div>
      <div
        class="edit-button c-outlineBtn c-outlineBtn--secondary c-btn--small"
        v-if="!editing"
        @click="clickEdit"
      >
        編集
      </div>
      <div v-if="editing">
        <div
          class="save-button c-btn c-btn--small c-btn--AnewsPrimary"
          @click="updateArticleInfo"
        >
          保存
        </div>
        <div class="spacing-12"></div>
        <div
          class="cancel-button c-outlineBtn c-btn--small c-outlineBtn--secondary"
          @click="cancelEdit"
        >
          キャンセル
        </div>
      </div>
    </div>
  </div>
</template>

<style lang="scss" scoped>
.article-info {
  background: #ffffff;
  padding: 24px 16px;
  border: 1px solid $color-gray400;
  box-sizing: border-box;
  border-radius: 4px;

  .heading {
    display: flex;
    justify-content: start;
    border-bottom: solid 1px $color-gray400;
    padding-bottom: 12px;
    margin-bottom: 12px;
  }

  .basic-info {
    .article-url {
      .url-link {
        display: flex;
        align-items: center;

        &:hover {
          text-decoration: underline;
        }

        .url {
          color: $color-green600;
          word-wrap: nowrap;
          white-space: nowrap;
          overflow-x: hidden;
          text-overflow: ellipsis;
        }
      }
    }
  }

  .tags {
    .tags-content {
      display: flex;
      flex-wrap: wrap;

      .tag-text {
        display: inline-block;
        max-width: 94px;
        word-wrap: nowrap;
        white-space: nowrap;
        overflow-x: hidden;
        text-overflow: ellipsis;
        margin-right: 2px;
      }
    }
  }

  .memo {
    .memo-content {
      white-space: pre-wrap;
      word-wrap: break-word;
      word-break: break-word;
      overflow-wrap: break-word;
    }

    .memo-textarea {
      width: 256px;
      max-width: 256px;
      height: 242px;
      outline: none;

      &::placeholder {
        color: $color-gray600;
      }
    }
  }

  .memo-empty,
  .tags-empty {
    color: $color-gray600;
  }

  .edit-button {
    width: 256px;
    border-color: $color-green600;
    color: $color-green600;

    &:hover {
      background: $color-green600;
      color: #ffffff;
    }
  }

  .cancel-button {
    width: 256px;
  }

  .save-button {
    width: 256px;
  }
}
</style>
